/**
 * GARAGE DOOR OPENER (2026-10-09, Matt: "roll up section garage doors should actually animate
 * like roll up sectional garage doors. They need to go up in sections and they should have
 * tracks and a motor with electrical hookup and a control button near the main garage entry
 * door from the house as well as a key pad on the jamb to the left of the garage door itself").
 *
 * The ONE layout every consumer reads, in a sectional door's local frame measured off the
 * wall FACE the door parks toward (its facing side): `d` runs from that face into the garage,
 * z from the opening's base. The 3D prototype draws the panels, tracks, spring shaft, rail and
 * motor from it; the walk-mode motion runs the panels along its track path; the derived
 * hookups (hookups.ts) put the opener's ceiling receptacle over its motor. A real sectional
 * hangs behind the opening on the inside face of the wall, overlapping the jambs and head —
 * so the panels leave the wall's thickness entirely, and nothing they travel through is wall.
 */

/** the operations that get a track, an opener and its controls */
export function isGarageOpenerOp(op: unknown): boolean {
  return op === 'sectional'
}

/** the panel's clearance off the wall face */
export const GARAGE_PANEL_GAP_FT = 0.04
/** how far the panel laps past the clear opening at each jamb and the head */
export const GARAGE_PANEL_LAP_FT = 0.08
/** the track radius — the standard 15" radius track (needs about 15" of headroom) */
export const GARAGE_TRACK_RADIUS_FT = 1.25
/** the opener motor head: centre past the parked door's back edge, and its size */
export const GARAGE_MOTOR_PAST_FT = 1.3
export const GARAGE_MOTOR_W_FT = 1.1
export const GARAGE_MOTOR_D_FT = 1.5
export const GARAGE_MOTOR_H_FT = 0.6
/** the wall control (UL 325: at least 5'-0" above the floor) and the exterior keypad */
export const GARAGE_BUTTON_Z_FT = 5
export const GARAGE_KEYPAD_Z_FT = 5
/** the keypad's centre past the opening edge, along the wall */
export const GARAGE_KEYPAD_PAST_FT = 0.75

export interface GarageOpenerLayout {
  /** the track path's distance off the wall face (the closed panel's centre plane) */
  trackD: number
  radius: number
  /** the top of the closed door — where the vertical track turns */
  headZ: number
  /** the horizontal track's height */
  horizZ: number
  /** how far the bottom of the door travels along the track to open fully */
  travel: number
  /** the horizontal track's far end, off the face */
  trackEndD: number
  /** the motor's centre off the face, and its vertical span */
  motorD: number
  motorZ0: number
  motorZ1: number
}

/** `doorTopZ` = the top of the closed panel stack, `doorBaseZ` its bottom (opening-local z) */
export function garageOpenerLayout(doorBaseZ: number, doorTopZ: number, panelT: number): GarageOpenerLayout {
  const R = GARAGE_TRACK_RADIUS_FT
  const H = Math.max(1, doorTopZ - doorBaseZ)
  const trackD = GARAGE_PANEL_GAP_FT + panelT / 2
  const travel = H + (Math.PI / 2) * R + 0.1
  const trackEndD = trackD + R + H + 0.4
  const motorD = trackD + R + H + GARAGE_MOTOR_PAST_FT
  const horizZ = doorTopZ + R
  return {
    trackD, radius: R, headZ: doorTopZ, horizZ, travel, trackEndD, motorD,
    motorZ0: horizZ - 0.1, motorZ1: horizZ - 0.1 + GARAGE_MOTOR_H_FT,
  }
}

/** the point on the track path at arc length `u` (u = z up the vertical run), as (d off the
 *  face, z) — vertical to the head, a quarter circle, then level back into the garage */
export function garageTrackPoint(L: Pick<GarageOpenerLayout, 'trackD' | 'radius' | 'headZ'>, u: number): [number, number] {
  if (u <= L.headZ) return [L.trackD, u]
  const a = u - L.headZ, R = L.radius, arcL = (Math.PI / 2) * R
  if (a <= arcL) {
    const th = a / R
    return [L.trackD + R * (1 - Math.cos(th)), L.headZ + R * Math.sin(th)]
  }
  return [L.trackD + R + (a - arcL), L.headZ + R]
}
